import React, { useState } from 'react';
import styled from 'styled-components';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import Overlay from './Overlay';
import Footer from './Footer';

/**
 * Main layout container using CSS Flexbox
 * This creates a flexible layout that adapts to different screen sizes
 */
const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh; /* Full viewport height */
`;

/**
 * Content wrapper that contains header, main content, and sidebar
 */
const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
`;

/**
 * Main content area where page content is rendered
 * Uses CSS margins to account for fixed header and sidebar
 */
const MainContent = styled.main`
  flex: 1; /* Takes up remaining space after sidebar */
  padding: 2rem;
  margin-top: 4rem; /* Space for fixed header */
  margin-left: 200px; /* Space for sidebar on desktop */
  background-color: #1a1a1a;
  color: #ffffff;
  display: flex;
  flex-direction: column;

  /* Responsive design: Remove sidebar margin on mobile */
  @media (max-width: 768px) {
    margin-left: 0;
    padding: 1.25rem; /* Slightly reduced padding on mobile */
  }
  
  /* Very small screens */
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

/**
 * Content area that grows to push footer down
 */
const PageContent = styled.div`
  flex: 1;
`;

/**
 * Layout Component - The main wrapper for all pages
 * 
 * This component provides the consistent structure for the entire app:
 * - Header (top navigation with menu button)
 * - Sidebar (navigation menu)
 * - Main content area (where individual pages render)
 * - Overlay (for mobile menu backdrop)
 * 
 * Key React concepts demonstrated:
 * - useState: Managing component state (sidebar open/closed)
 * - Props: Passing data and functions to child components
 * - Event handling: Functions that respond to user interactions
 * - Conditional rendering: Components that change based on state
 */
const Layout: React.FC = () => {
  // useState Hook: Creates state variable for sidebar visibility
  // Returns [currentValue, setterFunction]
  // Initial value is false (sidebar closed)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Event handler function: Toggles sidebar between open/closed
  // This function will be passed to the Header component
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen); // Flip the boolean value
  };

  // Event handler function: Always closes the sidebar
  // Used when user clicks on menu items or overlay
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <LayoutContainer>
      <ContentWrapper>
        {/* Header: Top navigation bar with menu button */}
        <Header onMenuClick={toggleSidebar} />
        
        {/* Sidebar: Navigation menu (visibility controlled by state) */}
        <Sidebar isOpen={isSidebarOpen} onItemClick={closeSidebar} />
        
        {/* Overlay: Dark background for mobile menu */}
        <Overlay isOpen={isSidebarOpen} onClick={closeSidebar} />
        
        {/* Main content area */}
        <MainContent>
          <PageContent>
            {/* 
              Outlet: Special React Router component
              This is where the content of each page (Home, You, etc.) gets rendered
              It's like a placeholder that gets filled with the current route's component
            */}
            <Outlet />
          </PageContent>
          
          {/* Footer: Copyright and links */}
          <Footer />
        </MainContent>
      </ContentWrapper>
    </LayoutContainer>
  );
};

export default Layout; 